import React from 'react';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import GoalCardSkeleton from './GoalCardSkeleton';

interface GoalsSectionSkeletonProps {
  title: string;
  icon: React.ReactNode;
  variant: 'blue' | 'green';
  cardCount?: number;
  showExpanded?: boolean;
  index?: number;
}

const GoalsSectionSkeleton: React.FC<GoalsSectionSkeletonProps> = ({
  title,
  icon,
  variant,
  cardCount = 3,
  showExpanded = true,
  index = 0
}) => {
  const gradientClasses = {
    blue: 'from-blue-50/60 to-white/40 dark:from-blue-950/20 dark:to-black/40 border-blue-200/50 dark:border-blue-900/30',
    green: 'from-green-50/60 to-white/40 dark:from-green-950/20 dark:to-black/40 border-green-200/50 dark:border-green-900/30'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
      className={`p-6 rounded-3xl shadow-[0_4px_20px_-1px_rgba(0,0,0,0.05)] 
        bg-gradient-to-br ${gradientClasses[variant]}
        backdrop-blur-sm border hover:shadow-lg`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="font-medium text-lg">{title}</h3>
          <Skeleton className="h-6 w-6 rounded-full ml-2" />
        </div>
        {variant === 'green' && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-5 w-5 rounded" />
          </div>
        )}
      </div>

      {/* Content */}
      {showExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ delay: (index * 0.05) + 0.1, duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
          className="space-y-4"
        >
          {cardCount === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Skeleton className="h-16 w-16 rounded-full mb-4" />
              <Skeleton className="h-5 w-48 mb-2" />
              <Skeleton className="h-10 w-56 rounded-lg" />
            </div>
          ) : (
            Array.from({ length: cardCount }).map((_, i) => (
              <GoalCardSkeleton key={`goal-skeleton-${i}`} index={i} />
            ))
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default GoalsSectionSkeleton;
